import { useState } from 'react';
import Image from 'next/image';
import classNames from 'classnames';

import {
  SectionTitle,
  Tab,
  TabContent,
  TabContentItem,
  TabPanel,
  Tabs,
  Button,
  Banner,
} from '@/ui/index';
import {
  Arrow,
  Ranking,
  FillUser,
  Bundle,
  People,
  Photo,
  Stability,
  Coverage,
} from '@/icons/index';
import SvgNote from '@/icons/Note';
import CardBlock from '@/ui/DocumentCard/DocumentCard';
import CoverageMap from '@/ui/CoverageMap/CoverageMap';

import DirectorItem from '../DirectorItem/DirectorItem';
import ColorCards from '../ColorCards/ColorCards';
import { SliderContainer, NewsItem } from '../../common';

import styles from './AboutUsPage.module.scss';

const AboutUsPage = ({
  data: {
    companyBlock,
    managementSlider,
    directorsSlider,
    newsSlider,
    banner,
    colorCards,
    infoBlock,
    sustainabilityBlock,
  },
}) => {
  const [activeTab, setActiveTab] = useState(0);

  const sliderOptions1 = {
    slidesToShow: 1,
    infinite: true,
    responsive: [
      {
        breakpoint: 1400,
        settings: {
          arrows: false,
        },
      },
    ],
  };

  const sliderOptions2 = {
    slidesToShow: 3,
    responsive: [
      {
        breakpoint: 1400,
        settings: {
          arrows: false,
        },
      },
      {
        breakpoint: 960,
        settings: {
          arrows: false,
          slidesToShow: 2,
        },
      },
      {
        breakpoint: 640,
        settings: {
          arrows: false,
          slidesToShow: 1,
        },
      },
    ],
  };

  const renderDirectorItem = (item, key) => (
    <DirectorItem item={item} key={key} />
  );

  const renderTab = (item, key) => (
    <Tab
      className={classNames([
        styles.tab,
        { [styles.active]: activeTab === key },
      ])}
      onClick={() => setActiveTab(key)}
      key={key}
    >
      {item.tab}
    </Tab>
  );

  const renderTabContent = (item, key) => (
    <TabContentItem
      className={classNames({
        [styles.tabContentItem]: activeTab === 0,
      })}
      key={key}
    >
      <div className={`${styles.infoBlock} grid-extend equal`}>
        <div className={styles.info}>
          <div className={styles.note}>{item?.content?.note}</div>
          <h2 className={styles.title}>{item?.content?.title}</h2>
          <p className={styles.text}>{item?.content?.text}</p>

          <Button
            href={item?.content?.link}
            Component={'router'}
            className={styles.button}
          >
            Read more <Arrow width={24} />
          </Button>
        </div>

        <div className={styles.image}>
          <Image
            src={item?.content?.image}
            alt='Bakcell'
            title='Bakcell'
            loading='lazy'
            layout='fill'
          />
        </div>
      </div>
    </TabContentItem>
  );

  const renderManagerCard = (item, key) => {
    return (
      <Banner
        item={item}
        note={item.position}
        buttonHide
        type={'white'}
        key={key}
      />
    );
  };

  return (
    <div>
      {!!companyBlock && (
        <div className={`${styles.companyBlock} wrapper section`}>
          <SectionTitle
            Icon={Ranking}
            className={styles.title}
            titleModify='title--large'
          >
            {companyBlock?.title}
          </SectionTitle>
          {!!companyBlock?.tabs?.length && (
            <Tabs active={activeTab}>
              <TabPanel className={styles.tabToggles}>
                {companyBlock?.tabs.map(renderTab)}
              </TabPanel>
              <TabContent>
                {companyBlock?.tabs.map(renderTabContent)}
              </TabContent>
            </Tabs>
          )}
        </div>
      )}

      {!!managementSlider?.list?.length && (
        <SliderContainer
          title={managementSlider?.title}
          icon={FillUser}
          addClass={styles.bannerSlider}
          options={sliderOptions1}
        >
          {managementSlider?.list?.map(renderManagerCard)}
        </SliderContainer>
      )}

      {!!directorsSlider && (
        <SliderContainer
          title={directorsSlider?.title}
          icon={People}
          options={sliderOptions2}
          addClass={styles.directors}
        >
          {directorsSlider?.list.map(renderDirectorItem)}
        </SliderContainer>
      )}

      {!!newsSlider && (
        <SliderContainer
          title={newsSlider?.title}
          linkText={newsSlider?.linkText}
          link={newsSlider?.link}
          icon={SvgNote}
          options={sliderOptions2}
        >
          {newsSlider?.list?.map((item, key) => (
            <NewsItem data={item} key={key} />
          ))}
        </SliderContainer>
      )}

      {!!banner && (
        <Banner item={banner} type={'white'} href={'/about-us/vacancies'} />
      )}

      {!!colorCards && (
        <ColorCards
          data={colorCards}
          titleIcon={Bundle}
          className='wrapper section'
        />
      )}

      {!!infoBlock && (
        <div className={'wrapper section'}>
          <SectionTitle
            Icon={Photo}
            className={styles.title}
            titleModify='title--large'
          >
            {infoBlock?.title}
          </SectionTitle>

          <div className={`${styles.infoBlock} grid-extend equal`}>
            <div
              className={styles.info}
              dangerouslySetInnerHTML={{ __html: infoBlock?.content }}
            />
            <div className={styles.image}>
              <Image
                src={infoBlock?.image}
                alt='Bakcell'
                title='Bakcell'
                loading='lazy'
                layout='fill'
              />
            </div>
          </div>
        </div>
      )}

      <div className={'wrapper section'}>
        <SectionTitle
          Icon={Coverage}
          className={styles.title}
          titleModify='title--large'
        >
          Coverage map
        </SectionTitle>
        <CoverageMap />
      </div>

      {!!sustainabilityBlock && (
        <CardBlock
          Icon={Stability}
          data={sustainabilityBlock}
          className={'wrapper section'}
        />
      )}
    </div>
  );
};

export default AboutUsPage;
